import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';

import { routeTree } from './routeTree.gen';
import { queryClient } from '@/lib/queryClient';
import { initPostHog } from '@/lib/posthog';
import { installChunkReloadHandler } from '@/lib/chunkReload';
import { RouteErrorFallback } from '@/components/RouteError';
import { env } from '@/lib/env';
import { client as apiClient } from '@/api/generated/client.gen';
import { fetchCsrfToken, clearCsrfToken } from '@/lib/api';
import './index.css';

// Point the generated SDK client at the cross-origin API host and ensure
// session cookies are sent. Without this the SDK uses relative URLs and
// requests land on the SPA origin (Cloudflare Pages catch-all returns
// index.html → "data is undefined" in TanStack Query).
apiClient.setConfig({
  baseUrl: env.apiOrigin,
  credentials: 'include',
});

// Attach a CSRF token to every mutating request — server enforces
// double-submit. The api() helper does the same thing for hand-rolled
// fetches; this interceptor covers generated SDK calls (bulkSet*,
// triggerFullSync, reply, close, no-reply, create/update/delete templates
// and rules, etc).
const MUTATING_METHODS = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);
apiClient.interceptors.request.use(async (req: Request) => {
  if (!MUTATING_METHODS.has(req.method.toUpperCase())) return req;
  const token = await fetchCsrfToken();
  // Request.headers is immutable after construction — clone with a new
  // Headers object that includes the CSRF token.
  const headers = new Headers(req.headers);
  headers.set('X-CSRF-Token', token);
  return new Request(req, { headers });
});

// On 403 with a CSRF body, drop the cached token so the next request
// fetches a fresh one. Mirrors the retry-once behaviour in api().
apiClient.interceptors.response.use(async (res: Response) => {
  if (res.status === 403) {
    const text = await res.clone().text();
    if (text.toLowerCase().includes('csrf')) clearCsrfToken();
  }
  if (res.status === 401) clearCsrfToken();
  return res;
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  scrollRestoration: true,
  // Loader errors render in place of the failing route (inside the shell):
  // a friendly AccessDenied for backend 403s, a plain alert otherwise.
  defaultErrorComponent: RouteErrorFallback,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

initPostHog();

// Recover from stale/mis-served lazy chunks after a deploy (blank page +
// "text/html is not a valid JavaScript MIME type"). Must be installed before
// the router mounts and starts importing route chunks.
installChunkReloadHandler();

const container = document.getElementById('root');
if (!container) {
  throw new Error('root element missing from index.html');
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
